import React, { useEffect, useState } from "react";
import { Save, Plus, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "./api";
import { Btn, Card, Field, Input, Textarea, ImagePicker, ListEditor } from "./ui";
import { IndexingCard } from "./IndexingCard";

export default function SettingsTab({ section, onChanged }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => { adminApi.settings().then(setS).catch((e) => toast.error(errText(e))); }, []);
  if (!s) return <p className="text-emerald-50/60 text-sm">Loading...</p>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setS((p) => ({ ...p, content: { ...(p.content || {}), [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { setS(await adminApi.saveSettings(s)); toast.success("Settings saved"); onChanged?.(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const changePw = async () => {
    try { await adminApi.changePassword(pw.current_password, pw.new_password); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); } catch (e) { toast.error(errText(e)); }
  };

  const SaveBar = () => <div className="flex justify-end"><Btn data-testid="settings-save" onClick={save} disabled={busy}><Save size={15} /> {busy ? "Saving..." : "Save changes"}</Btn></div>;

  if (section === "links") return (
    <div className="space-y-4">
      <Card title="Download & Telegram links">
        <Field label="Global download link" hint="Har game ke download button pe ye link (jab tak game ka apna link na ho)"><Input data-testid="settings-download-url" value={s.downloadUrl || ""} onChange={(e) => set("downloadUrl", e.target.value)} /></Field>
        <Field label="Telegram link" hint="Khali = Telegram buttons chhup jayenge"><Input data-testid="settings-telegram" value={s.telegramLink || ""} onChange={(e) => set("telegramLink", e.target.value)} placeholder="https://t.me/..." /></Field>
        <SaveBar />
      </Card>
      <Card title="Branding">
        <Field label="Site name"><Input data-testid="settings-site-name" value={s.siteName || ""} onChange={(e) => set("siteName", e.target.value)} /></Field>
        <ImagePicker label="Logo" value={s.logo} onChange={(v) => set("logo", v)} testId="settings-logo" />
        <ImagePicker label="Favicon (browser tab icon)" value={s.favicon} onChange={(v) => set("favicon", v)} testId="settings-favicon" />
        <SaveBar />
      </Card>
      <Card title="Admin password">
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Current password"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
          <Field label="New password (min 6)"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
        </div>
        <div className="flex justify-end"><Btn variant="green" onClick={changePw}><KeyRound size={15} /> Change password</Btn></div>
      </Card>
    </div>
  );

  if (section === "seo") return (
    <div className="space-y-4">
      <Card title="Home page SEO">
        <Field label="Site URL (sitemap & indexing ke liye)" hint="Aapka live domain, e.g. https://mahagamesapps.com"><Input data-testid="seo-site-url" value={s.siteUrl || ""} onChange={(e) => set("siteUrl", e.target.value)} placeholder="https://mahagamesapps.com" /></Field>
        <Field label="Meta title"><Input data-testid="seo-title" value={s.metaTitle || ""} onChange={(e) => set("metaTitle", e.target.value)} /></Field>
        <Field label="Meta description"><Textarea data-testid="seo-desc" value={s.metaDescription || ""} onChange={(e) => set("metaDescription", e.target.value)} /></Field>
        <Field label="Meta keywords (one per line)"><ListEditor testId="seo-keywords" value={s.metaKeywords || []} onChange={(v) => set("metaKeywords", v)} /></Field>
        <Field label="Popular searches (footer, one per line)"><ListEditor testId="seo-popular" value={s.popularSearches || []} onChange={(v) => set("popularSearches", v)} /></Field>
        <Field label="Google site verification code"><Input value={s.googleVerification || ""} onChange={(e) => set("googleVerification", e.target.value)} /></Field>
        <SaveBar />
      </Card>
      <IndexingCard />
    </div>
  );

  if (section === "content") {
    const c = s.content || {};
    return (
      <Card title="Home page content">
        <Field label="Hero title" hint='"All" ke baad wala hissa gold dikhega'><Input data-testid="content-hero-title" value={c.heroTitle || ""} onChange={(e) => setC("heroTitle", e.target.value)} /></Field>
        <Field label="Hero lead text"><Textarea value={c.heroLead || ""} onChange={(e) => setC("heroLead", e.target.value)} /></Field>
        <Field label="Intro title"><Input value={c.introTitle || ""} onChange={(e) => setC("introTitle", e.target.value)} /></Field>
        <Field label="Intro text"><Textarea rows={5} value={c.introText || ""} onChange={(e) => setC("introText", e.target.value)} /></Field>
        <Field label="Maha Yono Games box title"><Input value={c.mahaTitle || ""} onChange={(e) => setC("mahaTitle", e.target.value)} /></Field>
        <Field label="Maha Yono Games box text"><Textarea rows={5} value={c.mahaText || ""} onChange={(e) => setC("mahaText", e.target.value)} /></Field>
        <Field label="Why Choose Maha Yono Games text"><Textarea rows={5} value={c.whyMahaText || ""} onChange={(e) => setC("whyMahaText", e.target.value)} /></Field>
        <SaveBar />
      </Card>
    );
  }

  if (section === "reviews") {
    const r = s.reviews || [];
    const upd = (i, k, v) => set("reviews", r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
    return (
      <Card title={`Player reviews · ${r.length}`} action={<Btn data-testid="add-review-btn" variant="green" onClick={() => set("reviews", [...r, { name: "", city: "", game: "", rating: 5, text: "" }])}><Plus size={15} /> Add</Btn>}>
        <div className="space-y-3">
          {r.map((x, i) => (
            <div key={i} data-testid={`review-row-${i}`} className="bg-[#013013] border border-[#1c5f35] rounded-xl p-3 grid sm:grid-cols-[1fr_1fr_1fr_80px_auto] gap-2 items-start">
              <Input value={x.name} placeholder="Name" onChange={(e) => upd(i, "name", e.target.value)} />
              <Input value={x.city} placeholder="City" onChange={(e) => upd(i, "city", e.target.value)} />
              <Input value={x.game} placeholder="Game" onChange={(e) => upd(i, "game", e.target.value)} />
              <Input type="number" min={1} max={5} value={x.rating} onChange={(e) => upd(i, "rating", Number(e.target.value))} />
              <Btn variant="ghost" onClick={() => set("reviews", r.filter((_, j) => j !== i))}><X size={15} /></Btn>
              <Textarea rows={2} className="sm:col-span-5" value={x.text} placeholder="Review text" onChange={(e) => upd(i, "text", e.target.value)} />
            </div>
          ))}
        </div>
        <div className="mt-4"><SaveBar /></div>
      </Card>
    );
  }
  return null;
}
